import { useModerationQueue } from '../../events/queries';
import { EventCard } from './EventCard';
import { EmptyState, ErrorState, Heading, Spinner, Stack, Text } from '../../ui';

/**
 * The moderation queue: events awaiting review, oldest first. Administrators only.
 *
 * An empty queue is the healthy state and reads that way — a calm "nothing waiting", not a void.
 * Each row links to the event detail, where an administrator approves, rejects or edits it.
 *
 * @returns The moderation queue screen.
 */
export function ModerationQueuePage(): React.JSX.Element {
  const { data, isPending, isError, error, refetch } = useModerationQueue();

  return (
    <Stack gap="xl">
      <Heading level={1}>Moderation queue</Heading>

      {isPending && (
        <Stack gap="md" align="center">
          <Spinner label="Loading the queue" />
          <Text muted>Loading…</Text>
        </Stack>
      )}

      {isError && <ErrorState message={error.message} onRetry={() => void refetch()} />}

      {!isPending && !isError && data.length === 0 && (
        <EmptyState
          title="Nothing waiting"
          description="Every submitted listing has been reviewed. New submissions will appear here."
        />
      )}

      {!isPending && !isError && data.length > 0 && (
        <div className="gm-event-list">
          {data.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      )}
    </Stack>
  );
}
